import React, {useCallback, useEffect, useId, useRef, useState} from 'react';
import {
  Button,
  ButtonWithPopout,
  HelpTip,
  Horizontal,
  LayoutIcon,
  PopOut,
  ThemeName,
  Vertical,
} from '@taopix/taopix-design-system';
import {ButtonProps} from '@taopix/taopix-design-system/dist/types/Components/Button/Button';
import {HelpTipProps} from '@taopix/taopix-design-system/dist/types/Components/HelpTip/HelpTip';
import {useTranslation} from 'react-i18next';
import {useUiState} from '../../Context/UserInterface/UserInterfaceContext';
import {UserInterfaceActions} from '../../Context/UserInterface/UserInterfaceActionTypes';
import {PopOutProps} from '@taopix/taopix-design-system/dist/types/Components/PopOut/PopOut';
import AutoLayoutOptionButtons from './AutoLayoutOptionButtons';
import {PageDetectionActionType, PageStyleType} from '@taopix/taopix-core';
import {useTaopix} from '../../Context/TaopixContext';
import {ShuffleLayoutArgs} from '@taopix/taopix-core/dist/types/types';
import useShuffleLayoutButtons from '../../../Hooks/useShuffleLayoutButtons';
import {onSetLayout} from '../../../Utils/Layout';
import {getScrollParent} from '../../../Utils/Common';

type AutoLayoutButtonProps = {
  buttonProps: ButtonProps;
  disabled?: boolean;
};

export type ShuffleLayoutButtonState = {
  spread: boolean;
  left: boolean;
  right: boolean;
};

const AutoLayoutButton = ({buttonProps, disabled}: AutoLayoutButtonProps) => {
  const app = useTaopix();
  const {t} = useTranslation();
  const ui = useUiState();

  const [showApplyPopout, setShowApplyPopout] = useState(false);
  const [showHelpTip, setShowHelpTip] = useState(false);
  const [buttonState, , api] = useShuffleLayoutButtons();
  const [alert, setAlert] = useState('');

  // If less then two options then no need to display the popout we will just apply
  const lessThanTwoOptions = Object.values(buttonState).filter(truthy => truthy).length < 2;

  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    // close the alert whenever the page changes
    setAlert('');
  }, [ui.state.pageNumber]);

  useEffect(() => {
    // if no buttons are enabled, close the auto layout buttons
    if (lessThanTwoOptions) {
      setShowApplyPopout(false);
    }
  }, [buttonState]);

  useEffect(() => {
    if (alert.length > 0) {
      setTimeout(() => {
        onCloseAlert();
      }, 3000);
    }
  }, [alert]);

  /*
    Use the showHelpTip state to display the helptip when a user adds a picture box or text box to their page.
    Only show the help tip on large screen
  */

  const onRequestLayout = useCallback(
    (payload: ShuffleLayoutArgs, detectionArea: PageDetectionActionType) => {
      onSetLayout(app, payload, detectionArea, api, setAlert);
    },
    [api]
  );

  const onApplyPageStyle = useCallback(() => {
    if (!api) {
      return void 0;
    }

    const payload: ShuffleLayoutArgs = {
      pageNumber: api.getPageNumber(),
      pageType: api.getDisplayProps().pageIsCover ? PageStyleType.Cover : PageStyleType.Single,
      type: api.getDisplayProps().pageIsCover ? 'spread' : api?.insideLeft() ? 'right' : 'left',
    };

    onRequestLayout(
      payload,
      api.getDisplayProps().pageIsCover
        ? PageDetectionActionType.CurrentSpread
        : api?.insideLeft()
        ? PageDetectionActionType.CurrentRight
        : PageDetectionActionType.CurrentLeft
    );
  }, [api]);

  const onClickAutoLayout = useCallback(() => {
    if (!api || disabled) {
      return void 0;
    }

    // if the page is a cover or a single page, call apply
    if (api.getDisplayProps().pageIsCover || !api.getDisplayProps().pageIsSpreads) {
      onApplyPageStyle();
    } else {
      // if we have less than 2 buttons to show, call apply
      if (lessThanTwoOptions) {
        return onApplyPageStyle();
      }

      if (ui.state.isSmallScreen) {
        // Small screen auto layout options are shown in a toolpalette
        ui.dispatch(
          UserInterfaceActions.setToolPaletteSection('pageActionsPalette/autoLayoutOptionsPalette')
        );
      } else {
        // Large screen auto layout options are shown in a popout
        setShowApplyPopout(!showApplyPopout);
      }
    }
  }, [api, buttonState, disabled]);

  const onCloseAlert = () => {
    setAlert(current => '');
  };

  const helpTipProps: Partial<HelpTipProps> = {
    // **** New string required ****
    message: 'Try applying a pre-designed layout',
    onClose: () => setShowHelpTip(false),
    anchorId: 'autoLayoutButton',
    anchorOrigin: {vertical: Vertical.Top, horizontal: Horizontal.Centre},
    transformOrigin: {vertical: Vertical.Bottom, horizontal: Horizontal.Centre},
    offset: {x: 0, y: -10},
    showAfter: 500,
    popDirection: 'up',
    arrow: true,
  };

  const buttonId = 'autoLayoutButton-' + useId().replaceAll(':', '');
  const popoutId = 'autoLayoutPopout-' + ui.state.pageNumber;

  const buttonPopOutProps: Partial<PopOutProps> = {
    id: popoutId,
    anchorId: buttonId,
    anchorOrigin: {vertical: Vertical.Top, horizontal: Horizontal.Centre},
    transformOrigin: {vertical: Vertical.Bottom, horizontal: Horizontal.Centre},
    onClose: () => setShowApplyPopout(false),
    excludeIdFromClickOutside: buttonId,
    arrow: true,
    popDirection: 'up',
    offset: {x: 0, y: -10},
    theme: 'tpx-theme-workarea',
    themeTip: 'Workarea',
    contentTheme: ThemeName.Popouts,
    className: 'flex',
  };

  const warningPopOutProps: Partial<PopOutProps> = {
    id: 'autoLayout-warning' + ui.state.pageNumber,
    arrow: true,
    anchorId: ui.state.isSmallScreen ? 'pageActionsPalette' : buttonId,
    className: 'flex flex-col p-md space-y-sm max-w-[300rem]',
    anchorOrigin: {vertical: Vertical.Top, horizontal: Horizontal.Centre},
    transformOrigin: {vertical: Vertical.Bottom, horizontal: Horizontal.Centre},
    popDirection: 'up',
    offset: {x: 0, y: -10},
    onClose: () => onCloseAlert(),
    excludeIdFromClickOutside: [buttonId],
    theme: 'tpx-theme-workarea',
    themeTip: 'Workarea',
    contentTheme: ThemeName.Warning,
  };

  const closePopout = useCallback(() => {
    if (showApplyPopout) {
      // Remove the scroll handler from the scrollParent
      const scrollElement = getScrollParent(buttonRef?.current);
      scrollElement?.removeEventListener('scroll', closePopout);
      // Close the popout
      setShowApplyPopout(false);
    }
  }, [showApplyPopout]);

  useEffect(() => {
    const scrollElement = getScrollParent(buttonRef?.current);
    // Add a scroll listener to the scrollParent to close the popout when it is scrolled.
    if (showApplyPopout) {
      // Add the listener after a delay to avoid being triggered by the scrollIntoView method used when opening
      scrollElement?.addEventListener('scroll', closePopout);
    } else {
      setShowApplyPopout(false);
    }
    return () => {
      // Remove the handler
      scrollElement?.removeEventListener('scroll', closePopout);
    };
  }, [showApplyPopout]);

  // Will either return a normal button or button with popout to handle displaying multiple options
  const autoLayoutButton = lessThanTwoOptions ? (
    <>
      <Button
        ref={buttonRef}
        id={buttonId}
        label={t('str_LabelAutoLayout')}
        startIcon={<LayoutIcon />}
        onClick={onClickAutoLayout}
        disabled={disabled}
        showToolTip={false}
        {...buttonProps}
      />
    </>
  ) : (
    <ButtonWithPopout
      open={showApplyPopout}
      closeOnInteractions={false}
      onOpen={() => {
        onClickAutoLayout();
      }}
      onClose={() => {
        closePopout();
      }}
      buttonProps={{
        id: buttonId,
        ref: buttonRef,
        label: t('str_LabelAutoLayout'),
        startIcon: <LayoutIcon />,
        disabled,
        showToolTip: false,
        ...buttonProps,
      }}
      popoutProps={{...buttonPopOutProps}}
    >
      <>
        <AutoLayoutOptionButtons
          buttonState={buttonState}
          api={api}
          onRequestLayout={onRequestLayout}
        />
      </>
    </ButtonWithPopout>
  );

  return (
    <>
      {autoLayoutButton}
      <PopOut id={'noStylesAvailable'} open={alert.length > 0} {...warningPopOutProps}>
        {alert}
      </PopOut>
      <HelpTip open={showHelpTip} {...helpTipProps} />
    </>
  );
};
export default AutoLayoutButton;
